import { Box, ListItem, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import dateTimeFormatter from "utils/dateTimeFormatter";

const formatTimestamp = (time: number) => {
    const date = new Date(time * 1000); // Convert Unix timestamp to milliseconds
    const now = new Date();

    // Reset time to midnight for date comparison
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const timestampDate = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate()
    );

    // Format time as HH:MM AM/PM
    const formatTime = (d: Date) => {
        let hours = d.getHours();
        const minutes = d.getMinutes();
        const ampm = hours >= 12 ? "PM" : "AM";
        hours = hours % 12;
        hours = hours || 12; // the hour '0' should be '12'
        const minutesStr = minutes < 10 ? "0" + minutes : minutes;
        return `${hours}:${minutesStr} ${ampm}`;
    };

    // Check if it's today
    if (timestampDate.getTime() === today.getTime()) {
        return `Today ${formatTime(date)}`;
    }

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    // Check if it's yesterday
    if (timestampDate.getTime() === yesterday.getTime()) {
        return `Yesterday ${formatTime(date)}`;
    }

    // For older dates, show the date time. e.g. 17/11/2025 09:00 PM
    return dateTimeFormatter.dateTime(date);
};

// Common styled components
export const StyledListItem = styled(ListItem)(
    () => `
    padding: 12px;
    background-color: #2F3A51;
    color: #ffffff;

    &:hover {
        background-color: #47536d;
    }

    &.unread {
        background-color: #2F3A51;
        &:hover {
            background-color: #47536d;
        }
    }

    &.read {
        background-color: #171f30;
        &:hover {
            background-color: #47536d;
        }
    }

    & .MuiListItemIcon-root {
        min-width: 0;
        margin-right: 16px;
        margin-top: 0;
    }
`
);

export const ContentBox = styled(Box)`
    background-color: #394662;
    padding: 10px;
    border-radius: 5px;
`;

export const SensorInfoBox = styled(Box)(
    () => `
    display: flex;
    flex-direction: row;
    gap: 12px;
    align-items: center;
    justify-content: space-between;
    background-color: #2F3A51;
    padding: 10px;
    border-radius: 5px;
`
);

// Common notification header component
interface NotificationHeaderProps {
    title: string;
    orgName: string;
    timestamp: number;
    type: string;
}

const defaultOrgName = "Default Organisation";

export const NotificationHeader = ({
    title,
    orgName,
    timestamp,
    type,
}: NotificationHeaderProps) => {
    const orgNameDisplay =
        orgName === defaultOrgName ? "Personal Account" : orgName;

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                width: "100%",
            }}
        >
            <Typography variant="body2">{title}</Typography>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 1.5,
                    color: "#828fab",
                }}
            >
                {orgNameDisplay && (
                    <>
                        <Typography variant="caption">
                            {orgNameDisplay}
                        </Typography>
                        <Typography variant="caption">•</Typography>
                    </>
                )}
                <Typography variant="caption">{type}</Typography>
                <Typography variant="caption">•</Typography>
                <Typography variant="caption">
                    {formatTimestamp(timestamp)}
                </Typography>
            </Box>
        </Box>
    );
};
